import React, { useEffect, useRef, useState } from "react";
import { ErrorMessage, Field, useFormikContext } from "formik";
import MenuItem from "@mui/material/MenuItem";
import Select from "@mui/material/Select";
import { lookupPostalPincode } from "services/postalAddressService";

const EMPTY_LOOKUP = { status: "idle", pinCode: "", areas: [], message: "" };
const optionKey = (option) => JSON.stringify([option.area, option.division, option.region, option.state]);
const inputClass = "mt-1 block w-full rounded-md border border-gray-300 text-sm focus:ring-0";

const PincodeAddressFields = ({ preserveSavedAddress = false, variant = "default", disabled = false }) => {
  const isMarketplace = variant === "marketplace";
  const { values, setValues, setFieldTouched, setFieldError, validateField } = useFormikContext();
  const [lookupEnabled, setLookupEnabled] = useState(!preserveSavedAddress);
  const [lookup, setLookup] = useState(EMPTY_LOOKUP);
  const [attempt, setAttempt] = useState(0);
  const requestRef = useRef(0);
  const preserveAddressRef = useRef(preserveSavedAddress);
  const pinCode = String(values.pincode || "");
  const activeLookup = lookup.pinCode === pinCode ? lookup : EMPTY_LOOKUP;
  const areas = activeLookup.areas;
  const matchingAreas = areas.filter((option) => option.area === values.area &&
    (!values.division || option.division === values.division) &&
    (!values.state || option.state === values.state));
  const selectedArea = matchingAreas.length === 1 ? matchingAreas[0] : undefined;
  const hasCurrentArea = Boolean(values.area) && !selectedArea;

  useEffect(() => {
    const requestId = ++requestRef.current;
    if (!lookupEnabled || !/^[1-9]\d{5}$/.test(pinCode)) {
      setLookup(EMPTY_LOOKUP);
      return undefined;
    }

    const controller = new AbortController();
    setLookup({ ...EMPTY_LOOKUP, pinCode, status: "loading" });
    const timer = setTimeout(() => {
      lookupPostalPincode(pinCode, controller.signal)
        .then((payload) => {
          if (controller.signal.aborted || requestRef.current !== requestId) return;
          setLookup({ ...payload, status: "success", message: "" });
          if (preserveAddressRef.current) return;

          const divisions = new Set(payload.areas.map((option) => option.division));
          const regions = new Set(payload.areas.map((option) => option.region));
          const states = new Set(payload.areas.map((option) => option.state));
          const division = divisions.size === 1 ? payload.areas[0].division : "";
          const region = regions.size === 1 ? payload.areas[0].region : "";
          const state = states.size === 1 ? payload.areas[0].state : "";
          setValues((current) => {
            if (String(current.pincode || "") !== pinCode) return current;
            return {
              ...current,
              area: current.area || (payload.areas.length === 1 ? payload.areas[0].area : ""),
              city: current.city || region,
              division: current.division || division,
              state: current.state || state,
              ...(isMarketplace ? { country: "India" } : {}),
            };
          }, false);
        })
        .catch((error) => {
          if (controller.signal.aborted || requestRef.current !== requestId) return;
          const notFound = error?.response?.data?.code === "PINCODE_NOT_FOUND";
          setLookup({
            ...EMPTY_LOOKUP,
            pinCode,
            status: notFound ? "not-found" : "unavailable",
            message: notFound
              ? "No postal areas found. Please check the pincode."
              : "Postal lookup is unavailable. Enter the area, city and state manually, or retry.",
          });
        });
    }, 300);

    return () => {
      clearTimeout(timer);
      controller.abort();
    };
  }, [pinCode, lookupEnabled, attempt, setValues, isMarketplace]);

  useEffect(() => {
    validateField("pincode");
    validateField("area");
  }, [lookup.status, lookup.pinCode, validateField]);

  const changePinCode = (event) => {
    const nextPinCode = event.target.value.replace(/\D/g, "").slice(0, 6);
    if (nextPinCode === pinCode) return;
    requestRef.current += 1;
    preserveAddressRef.current = false;
    setLookupEnabled(true);
    setLookup(EMPTY_LOOKUP);
    setFieldError("pincode", undefined);
    setFieldError("area", undefined);
    setValues((current) => ({
      ...current,
      pincode: nextPinCode,
      area: "",
      city: "",
      division: "",
      state: "",
      ...(isMarketplace ? { country: "India", district: "" } : {}),
    }), false);
  };

  const retryLookup = () => {
    preserveAddressRef.current = Boolean(values.area || values.city || values.division || values.state);
    setLookupEnabled(true);
    setAttempt((current) => current + 1);
  };

  const selectArea = (event) => {
    const selected = areas.find((option) => optionKey(option) === event.target.value);
    if (!selected) return;
    setValues((current) => ({
      ...current,
      area: selected.area,
      city: !isMarketplace && current.city && current.city !== (selectedArea?.region || selected.region) &&
        current.state === selected.state && (!current.division || current.division === selected.division)
        ? current.city : selected.region,
      division: selected.division,
      state: selected.state,
      ...(isMarketplace ? { country: "India" } : {}),
    }), false);
    setFieldTouched("area", true, false);
    setFieldError("area", undefined);
  };

  const validatePinCode = () => {
    if (pinCode && !/^[1-9]\d{5}$/.test(pinCode)) return "Pincode must be exactly 6 digits";
    if (!lookupEnabled || !pinCode) return undefined;
    if (activeLookup.status === "idle" || activeLookup.status === "loading") return "Please wait for the pincode lookup to finish.";
    if (activeLookup.status === "not-found" && !preserveAddressRef.current) return activeLookup.message;
    return undefined;
  };
  const validateArea = (value) => activeLookup.status === "success" && !String(value || "").trim()
    ? "Select an area for this pincode."
    : undefined;
  const blurAddressField = (name) => {
    setFieldTouched(name, true, false);
    validateField(name);
  };

  return (
    <>
      <p className="mb-3 mt-1 text-xs text-gray-500">Enter a pincode to find postal areas and fill city and state.</p>
      <div className={isMarketplace ? "grid grid-cols-1 gap-3 pb-3 sm:grid-cols-2" : "grid grid-cols-2 gap-3 pb-3 lg:grid-cols-7"}>
        <div>
          <label htmlFor="pincode" className="block text-sm font-medium">Pincode <span className="text-red-500" aria-hidden="true">*</span></label>
          <Field name="pincode" validate={validatePinCode}>
            {({ field }) => <input {...field} id="pincode" type="text" inputMode="numeric" autoComplete="postal-code" maxLength={6} value={pinCode} onChange={changePinCode} onBlur={() => blurAddressField("pincode")} aria-required="true" disabled={disabled} className={inputClass} />}
          </Field>
          <ErrorMessage name="pincode" component="div" className="mt-2 text-xs text-red-500" />
        </div>
        <div className={isMarketplace ? "" : "lg:col-span-2"}>
          <label htmlFor="area" className="block text-sm font-medium">{isMarketplace ? "Area / Post Office" : "Area"} <span className="text-red-500" aria-hidden="true">*</span></label>
          <Field name="area" validate={validateArea}>
            {({ field, meta }) => areas.length ? (
              <Select
                id="area"
                fullWidth
                size="small"
                displayEmpty
                disabled={disabled}
                value={selectedArea ? optionKey(selectedArea) : hasCurrentArea ? "__CURRENT__" : ""}
                onChange={selectArea}
                onBlur={() => blurAddressField("area")}
                error={Boolean(meta.touched && meta.error)}
                inputProps={{ "aria-label": "Area", "aria-required": "true", name: "area" }}
                sx={{ mt: 0.5, borderRadius: "6px", fontFamily: "inherit", fontSize: 14, "& .MuiSelect-select": { py: 1.1 }, "&.Mui-focused .MuiOutlinedInput-notchedOutline": { borderColor: "#FD6600" } }}
                MenuProps={{ PaperProps: { sx: { mt: 0.5, borderRadius: "12px", maxHeight: 300, maxWidth: "calc(100vw - 32px)" } } }}
              >
                <MenuItem value="" disabled>Select an area</MenuItem>
                {hasCurrentArea && <MenuItem value="__CURRENT__">{values.area} (current address)</MenuItem>}
                {areas.map((option) => <MenuItem key={optionKey(option)} value={optionKey(option)} sx={{ fontFamily: "inherit", fontSize: 14, whiteSpace: "normal", "&.Mui-selected": { backgroundColor: "#FFF0E3" } }}>
                  {option.area}<span className="ml-2 text-xs text-gray-500">{option.region}, {option.state}</span>
                </MenuItem>)}
              </Select>
            ) : <input {...field} value={field.value || ""} id="area" type="text" onBlur={() => blurAddressField("area")} aria-required="true" className={inputClass} disabled={disabled || (activeLookup.status === "loading" && !preserveAddressRef.current)} />}
          </Field>
          <ErrorMessage name="area" component="div" className="mt-2 text-xs text-red-500" />
        </div>
        <div className={isMarketplace ? "" : "lg:col-span-2"}>
          <label htmlFor="city" className="block text-sm font-medium">City <span className="text-red-500" aria-hidden="true">*</span></label>
          <Field id="city" name="city" type="text" onBlur={() => blurAddressField("city")} aria-required="true" disabled={disabled} className={inputClass} />
          <ErrorMessage name="city" component="div" className="mt-2 text-xs text-red-500" />
        </div>
        <div className={isMarketplace ? "" : "lg:col-span-2"}>
          <label htmlFor="state" className="block text-sm font-medium">State <span className="text-red-500" aria-hidden="true">*</span></label>
          <Field id="state" name="state" type="text" onBlur={() => blurAddressField("state")} aria-required="true" disabled={disabled} readOnly={Boolean(selectedArea)} className={`${inputClass} ${selectedArea ? "bg-gray-50" : ""}`} />
          <ErrorMessage name="state" component="div" className="mt-2 text-xs text-red-500" />
        </div>
        {!isMarketplace && <div className="col-span-2 lg:col-span-7">
          <label htmlFor="address" className="block text-sm font-medium">Address</label>
          <Field id="address" name="address" type="text" onBlur={() => setFieldTouched("address", true, false)} placeholder="Building, street and other address details" disabled={disabled} className={inputClass} />
          <ErrorMessage name="address" component="div" className="mt-2 text-xs text-red-500" />
        </div>}
      </div>
      <div aria-live="polite" className="flex flex-wrap items-center gap-2 text-xs text-gray-500">
        {activeLookup.status === "loading" && <span role="status" className="inline-flex items-center gap-2 text-[#C45100]"><span className="h-3 w-3 animate-spin rounded-full border-2 border-[#FFD6B8] border-t-[#FD6600]" />Finding postal areas...</span>}
        {activeLookup.message && <span className={activeLookup.status === "not-found" ? "text-red-600" : "text-[#A56812]"}>{activeLookup.message}</span>}
        {!lookupEnabled && <span>Your saved address is preserved.</span>}
        {(!lookupEnabled || activeLookup.status === "unavailable" || activeLookup.status === "not-found") && /^[1-9]\d{5}$/.test(pinCode) && (
          <button type="button" onClick={retryLookup} disabled={disabled} className="font-semibold text-[#C45100] underline underline-offset-2">{lookupEnabled ? "Retry lookup" : "Look up this pincode"}</button>
        )}
        {activeLookup.status === "success" && <span>City is filled from the postal region. You can edit it if needed.</span>}
      </div>
    </>
  );
};

export default PincodeAddressFields;
